import React, {useEffect} from "react";
import {Button, LinearProgress, Stack, Typography} from "@mui/material";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {DefaultError} from "@tanstack/query-core";
import {enqueueSnackbar} from "notistack";
import {
    disconnectFacebook,
    loadFacebookConnectUrl,
    loadFacebookPages,
    selectFacebookPage
} from "../../../service/facebook.service";
import {useAuthStore} from "../../../store/auth.store";
import {getMe} from "../../../service/auth.service";
import {FacebookInstruction} from "./FacebookInstruction";

export function FacebookIntegration({compact = false}: { compact?: boolean }) {
    const authStore = useAuthStore();
    const queryClient = useQueryClient();
    const hasFacebookAuthorization = !!authStore.user?.facebookAuthorized || !!authStore.user?.facebookConnected;

    useEffect(() => {
        const status = new URLSearchParams(document.location.search).get("facebook");
        if(status !== "authorized" && status !== "connected") {
            return;
        }
        getMe().then(response => {
            authStore.setUser(response.data);
            queryClient.invalidateQueries({queryKey: ["facebook-pages"]}).catch(() => void 0);
        }).catch(error => {
            enqueueSnackbar({message: "Failed to refresh Facebook connection: " + error.message, variant: "error"});
        });
    }, [authStore, queryClient]);

    const pagesQuery = useQuery({
        queryKey: ["facebook-pages"],
        enabled: hasFacebookAuthorization,
        refetchOnWindowFocus: false,
        queryFn: () => loadFacebookPages().then(response => response.data)
    });

    const connectMutation = useMutation<string, DefaultError, void>({
        mutationFn: () => loadFacebookConnectUrl().then(response => response.data.url),
        onSuccess: (url) => {
            window.location.href = url;
        },
        onError: (error) => {
            enqueueSnackbar({message: "Failed to start Facebook connection: " + error.message, variant: "error"});
        }
    });

    const selectPageMutation = useMutation({
        mutationFn: (pageId: string) => selectFacebookPage(pageId).then(response => response.data),
        onSuccess: (user) => {
            authStore.setUser(user);
            queryClient.invalidateQueries({queryKey: ["facebook-pages"]}).catch(() => void 0);
            enqueueSnackbar({message: "Facebook Page connected", variant: "success"});
        },
        onError: (error: DefaultError) => {
            enqueueSnackbar({message: "Failed to connect Facebook Page: " + error.message, variant: "error"});
        }
    });

    const disconnectMutation = useMutation({
        mutationFn: () => disconnectFacebook().then(response => response.data),
        onSuccess: (user) => {
            authStore.setUser(user);
            queryClient.removeQueries({queryKey: ["facebook-pages"]});
            enqueueSnackbar({message: "Facebook disconnected", variant: "success"});
        },
        onError: (error: DefaultError) => {
            enqueueSnackbar({message: "Failed to disconnect Facebook: " + error.message, variant: "error"});
        }
    });

    const connectedLabel = authStore.user?.facebookPageName || authStore.user?.facebookPageId;

    return <Stack gap={compact ? "10px" : "15px"}>
        <FacebookInstruction/>
        {connectedLabel && <Typography color={"success.main"}>
            Connected Page: {connectedLabel}
        </Typography>}
        {!connectedLabel && <Typography color={"textSecondary"}>
            No Facebook Page connected.
        </Typography>}

        <Stack flexDirection={"row"} gap={"10px"} flexWrap={"wrap"}>
            <Button
                variant={"contained"}
                disabled={connectMutation.isPending}
                onClick={() => connectMutation.mutate()}
            >
                {hasFacebookAuthorization ? "Reconnect Facebook" : "Connect Facebook"}
            </Button>
            {connectedLabel && <Button
                color={"inherit"}
                variant={"outlined"}
                disabled={disconnectMutation.isPending}
                onClick={() => disconnectMutation.mutate()}
            >
                Disconnect
            </Button>}
        </Stack>

        {(pagesQuery.isLoading || selectPageMutation.isPending || disconnectMutation.isPending) && <LinearProgress/>}

        {!!pagesQuery.data?.length && <Stack gap={"8px"}>
            <Typography variant={"body2"} color={"textSecondary"}>Available Pages</Typography>
            {pagesQuery.data.map(page => <Stack
                key={page.id}
                flexDirection={"row"}
                gap={"10px"}
                alignItems={"center"}
                justifyContent={"space-between"}
            >
                <Typography>{page.name}</Typography>
                <Button
                    size={"small"}
                    variant={page.connected ? "contained" : "outlined"}
                    disabled={!page.canPost || page.connected || selectPageMutation.isPending}
                    onClick={() => selectPageMutation.mutate(page.id)}
                >
                    {page.connected ? "Selected" : "Select"}
                </Button>
            </Stack>)}
        </Stack>}
    </Stack>
}
